import React, { useEffect, useState } from "react";
import { mediaApi, resolveMediaSrc } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, Copy, FileText } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "image", label: "Images" },
  { key: "video", label: "Videos" },
  { key: "doc", label: "Docs" },
];

const MediaLibrary = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { kind: filter };
      const { data } = await mediaApi.get(`/api/media`, { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      const params = filter === "all" ? {} : { kind: filter };
      try {
        const { data } = await mediaApi.get(`/api/media`, { params });
        if (alive) setItems(data);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [filter]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await mediaApi.post("/api/media/upload", fd);
      }
      await load();
      toast.success(`${files.length} skedar(ë) u ngarkuan`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ngarkimi dështoi");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Të fshihet ky skedar? Nuk mund të kthehet.")) return;
    await mediaApi.delete(`/api/media/${id}`);
    await load();
    toast.success("U fshi");
  };

  const copyUrl = (url) => {
    navigator.clipboard?.writeText(url);
    toast.success("URL u kopjua");
  };

  return (
    <div data-testid="media-library">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-medium text-zinc-900">Media Library</h1>
          <p className="text-sm text-zinc-500 mt-1">Ngarko foto e video. Përdori kudo në faqe nga butoni Library.</p>
        </div>
        <label className="cursor-pointer inline-flex items-center gap-2 bg-zinc-900 text-white text-xs tracking-wide px-4 py-2.5 rounded-md hover:bg-zinc-800 shrink-0" data-testid="media-upload-btn">
          {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} Ngarko
          <input type="file" hidden multiple accept="image/*,video/*,.pdf" onChange={onUpload} disabled={uploading} />
        </label>
      </div>

      <div className="mt-6 flex items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`media-filter-${f.key}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${filter === f.key ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-zinc-400" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-lg">Ende pa media. Ngarko të parën më sipër.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {items.map((m) => (
              <div key={m.id} className="group relative border border-zinc-200 rounded-md overflow-hidden bg-zinc-50" data-testid={`media-item-${m.id}`}>
                <div className="block w-full aspect-square">
                  {m.kind === "video" ? (
                    <video src={resolveMediaSrc(m.url)} className="w-full h-full object-cover" muted />
                  ) : m.kind === "doc" ? (
                    <span className="w-full h-full flex flex-col items-center justify-center gap-2 text-[#942222]">
                      <FileText size={30} />
                      <span className="text-[10px] uppercase tracking-wider text-zinc-500">PDF</span>
                    </span>
                  ) : (
                    <img src={resolveMediaSrc(m.url)} alt={m.filename} className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                  <button onClick={() => copyUrl(m.url)} className="bg-white/90 hover:bg-white text-zinc-700 rounded p-1" title="Copy URL" data-testid={`media-copy-${m.id}`}>
                    <Copy size={14} />
                  </button>
                  <button onClick={() => onDelete(m.id)} className="bg-white/90 hover:bg-white text-red-600 rounded p-1" title="Delete" data-testid={`media-delete-${m.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="px-2 py-1 text-[10px] text-zinc-600 truncate">{m.filename}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaLibrary;
